import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Spinner,
    InputGroup,
    InputLeftElement,
    Input,
    InputRightElement,
    Box,
    Td,
    Flex,
    Text,
    Button,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from 'react-query';
import * as yup from 'yup';
import { HiX } from 'react-icons/hi';

import { Pagination } from '@/components/Pagination';
import Icon from '@/components/Icon';
import { ArticlesListItem } from '@/features/Quiz/components/ArticlesListItem';
import {
    buildQuizzesArticleListParams,
    getArticle,
    postQuizzesArticles,
} from '@/features/Quiz/api/getArticles';
import type { SearchFormData } from '@/features/Quiz/components/ArticlesList';

type Props = {
    onSelectArticle: (domainId: number, sectionId?: number, title?: string) => void;
};

const searchInputSchema = yup.object().shape({
    keyword: yup.string(),
});

const isNumericKeyword = (value: string) => /^-?\d+$/.test(value.trim());

const stripHtml = (value: string) => value.replace(/<[^<>]+>/g, '').trim();

/**
 * Article picker for `/quiz/create?libraryType=VIDEO` only.
 * Empty input shows nothing. A numeric keyword resolves the article directly via
 * `getArticle` and auto-selects it. A text keyword shows a searchable list via the
 * legacy quiz article_list. Both paths include articles not yet mapped to a VIDEO quiz.
 */
export const VideoLibraryArticlesList = ({ onSelectArticle }: Props) => {
    const [activePage, setActivePage] = useState(1);
    const [total, setTotal] = useState(0);
    const [activeSearchKeyword, setActiveSearchKeyword] = useState('');

    const trimmedKeyword = activeSearchKeyword.trim();
    const isNumeric = trimmedKeyword !== '' && isNumericKeyword(trimmedKeyword);
    const isText = trimmedKeyword !== '' && !isNumeric;

    const { isFetching, data: articlesData } = useQuery(
        ['video-library-article-search', trimmedKeyword, activePage],
        () =>
            postQuizzesArticles(
                buildQuizzesArticleListParams(activePage, trimmedKeyword, 20, false, null, 0)
            ),
        { enabled: isText, useErrorBoundary: false }
    );

    const {
        isFetching: isResolvingArticle,
        isError: isArticleError,
    } = useQuery(
        ['video-library-article-by-id', trimmedKeyword],
        () => getArticle(Number(trimmedKeyword)),
        {
            enabled: isNumeric,
            retry: false,
            useErrorBoundary: false,
            onSuccess: (article) => {
                const domainId = article?.domain?.id;
                const sectionId = Number(article?.id);
                if (domainId == null || Number.isNaN(sectionId)) {
                    return;
                }
                const title = stripHtml(article?.domain?.title ?? '');
                onSelectArticle(domainId, sectionId, title);
            },
        }
    );

    const {
        register,
        reset,
        setValue,
        getValues,
        formState: { isSubmitting },
    } = useForm<SearchFormData>({
        resolver: yupResolver(searchInputSchema),
        defaultValues: {
            keyword: '',
        },
    });

    const commitSearch = useCallback(() => {
        setActivePage(1);
        setActiveSearchKeyword(getValues('keyword') ?? '');
    }, [getValues]);

    const handleSearchKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                commitSearch();
            }
        },
        [commitSearch]
    );

    const handleSearchSubmit = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            commitSearch();
        },
        [commitSearch]
    );

    const handleReset = useCallback(() => {
        reset();

        setActivePage(1);
        setActiveSearchKeyword('');
        setValue('keyword', '');
    }, [reset, setValue]);

    useEffect(() => {
        if (!articlesData?.result) {
            return setTotal(0);
        }

        if (typeof articlesData?.navigation?.total_pages === 'number') {
            setTotal(articlesData?.navigation?.total_pages ?? 0);
        } else {
            articlesData?.navigation?.total_items &&
                articlesData?.navigation?.items_on_page &&
                setTotal(Math.ceil(articlesData.navigation.total_items / articlesData.navigation.items_on_page));
        }
    }, [
        articlesData?.navigation?.total_items,
        articlesData?.navigation?.items_on_page,
        articlesData?.navigation?.total_pages,
        articlesData?.result,
    ]);

    const isLoadingIcon = isSubmitting || isFetching || isResolvingArticle;
    const showSpinner = (isNumeric && isResolvingArticle) || (isText && isFetching);
    const showArticleError = isNumeric && !isResolvingArticle && isArticleError;
    const showList = isText && !isFetching;

    const renderList = () => (
        <Table bgColor={'#ffffff'}>
            <Thead>
                <Tr>
                    <Th>ID</Th>
                    <Th>Title</Th>
                    <Th>Type</Th>
                    <Th>Actions</Th>
                </Tr>
            </Thead>
            <Tbody>
                {articlesData?.result?.length ? (
                    articlesData.result.map((article) => (
                        <ArticlesListItem
                            key={article.id}
                            article={article}
                            onSelectArticle={onSelectArticle}
                            libraryV2Select
                        />
                    ))
                ) : (
                    <Tr>
                        <Td>No articles found</Td>
                    </Tr>
                )}
            </Tbody>
        </Table>
    );

    return (
        <>
            <Box>
                <form onSubmit={handleSearchSubmit}>
                    <Flex minW={'400px'} gap={2} align="center">
                        <InputGroup>
                            <InputLeftElement pointerEvents={isSubmitting ? 'none' : 'auto'}>
                                {isLoadingIcon ? <Icon icon="loader" /> : <Icon icon="search" />}
                            </InputLeftElement>
                            <Input
                                placeholder="Search by Title or Section ID"
                                bg={'#ffffff'}
                                id={'keyword'}
                                {...register('keyword')}
                                onKeyDown={handleSearchKeyDown}
                            />
                            {activeSearchKeyword && (
                                <InputRightElement cursor="pointer" onClick={handleReset}>
                                    <HiX />
                                </InputRightElement>
                            )}
                        </InputGroup>
                        <Button
                            type="submit"
                            variant="brand"
                            flexShrink={0}
                            isLoading={isFetching || isResolvingArticle}
                        >
                            Search
                        </Button>
                    </Flex>
                </form>
                {showSpinner && (
                    <Flex justify="center" py={8}>
                        <Spinner />
                    </Flex>
                )}
                {showArticleError && (
                    <Text py={4} color="red.500">
                        Article not found for ID {trimmedKeyword}.
                    </Text>
                )}
                {showList && renderList()}
            </Box>
            {isText && articlesData && total > 1 && !isFetching && (
                <Pagination
                    currentPage={activePage}
                    totalPages={total}
                    handlePagination={(page: number) => setActivePage(page)}
                />
            )}
        </>
    );
};
